/*
 * Copyright 2026 North Carolina State University
 */

import { useState, useMemo } from 'react';

/**
 * Custom hook for sortable tables
 * @param {Array} data - The array of data to sort
 * @param {Object} config - Configuration object with default sort column and order
 * @returns {Object} - Object containing sorted data, sort handlers, and current sort state
 */
const useTableSort = (data, config = {}) => {
  const { defaultSortColumn = null, defaultSortOrder = 'asc' } = config;
  
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);

  // Handle sort column click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // If clicking the same column, toggle the order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it as sort column with ascending order
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  // Reset sort to default
  const resetSort = () => {
    setSortColumn(defaultSortColumn);
    setSortOrder(defaultSortOrder);
  };

  // Sort the data
  const sortedData = useMemo(() => {
    if (!sortColumn || !data) return data;

    const sorted = [...data].sort((a, b) => {
      // Get values, handling nested properties (e.g., 'facility.facility_name')
      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };

      let aVal = getNestedValue(a, sortColumn);
      let bVal = getNestedValue(b, sortColumn);

      // Handle undefined/null values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Convert to strings for comparison if they're not numbers
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      // Compare values
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortColumn, sortOrder]);

  return {
    sortedData,
    sortColumn,
    sortOrder,
    handleSort,
    resetSort,
  };
};

export default useTableSort;

